/**
 * The site's address ("/"): whichever home page the owner made live, for everyone, signed in or
 * not. Before this, a signed-out visitor was redirected to /flagship and a signed-in one got the
 * layout-letter dashboard, so "the home page" depended on who you were and needed a deploy to
 * change. Now one setting decides it (siteSettings.ts), and an admin sees the switcher.
 *
 * `?layout=<letter>` still opens the old lab dashboard, for links already sent around.
 */
import React, { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation } from "react-router-dom";
import { userContext } from "../../../../stores/users/userContext";
import HomePageDashboard from "../HomePageDashboard";
import FlagshipPage from "../components/FlagshipPage";
import LayoutWPage from "../components/home3/LayoutWPage";
import LayoutYPage from "../components/home3/LayoutYPage";
import LayoutZPage from "../components/home3/LayoutZPage";
import SimplePage from "../simple/SimplePage";
import SiteLayoutSwitcher from "./SiteLayoutSwitcher";
import {
  FALLBACK,
  Preview,
  SiteSettings,
  fetchSiteSettings,
  readCachedSettings,
  readPreview,
  saveSiteSettings,
  writePreview,
} from "./siteSettings";

/** How long a first-time visitor waits for the setting before we show the fallback page. */
const WAIT_MS = 1500;

export const HomeLayoutView: React.FC<{ view: Preview }> = ({ view }) => {
  switch (view.layout) {
    case "simple":
      return <SimplePage skin={view.skin} headline={view.headline} />;
    case "w":
      return <LayoutWPage />;
    case "y":
      return <LayoutYPage />;
    case "z":
      return <LayoutZPage />;
    default:
      return <FlagshipPage />;
  }
};

const SiteHome: React.FC = () => {
  const { search } = useLocation();
  const userStore = useContext(userContext);
  const authed = userStore.isUserAuth();
  const [settings, setSettings] = useState<SiteSettings | null>(() => readCachedSettings());
  const [ready, setReady] = useState<boolean>(() => readCachedSettings() !== null);
  const [preview, setPreview] = useState<Preview | null>(() => readPreview());

  // Re-asked after sign-in: the answer also says whether THIS person may switch it.
  useEffect(() => {
    let alive = true;
    const t = window.setTimeout(() => alive && setReady(true), WAIT_MS);
    fetchSiteSettings()
      .then((s) => alive && setSettings(s))
      .catch((): void => undefined)
      .finally(() => alive && setReady(true));
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [authed]);

  if (new URLSearchParams(search).get("layout")) return <HomePageDashboard />;
  // A blank page the colour of the paper for a moment beats flashing one home page then another.
  if (!ready) return <div style={{ minHeight: "100vh", background: "#f7f2ee" }} />;

  const s = settings || FALLBACK;
  const live: Preview = { layout: s.home_layout, skin: s.home_skin, headline: s.home_headline };
  const canEdit = authed && s.can_edit;
  const shown = canEdit && preview ? preview : live;

  const changePreview = (p: Preview | null) => {
    writePreview(p);
    setPreview(p);
    try {
      window.scrollTo(0, 0);
    } catch (e) {
      /* ignore */
    }
  };
  const makeLive = async (p: Preview) => {
    const saved = await saveSiteSettings(p);
    setSettings(saved);
    changePreview(null);
  };

  return (
    <>
      <HomeLayoutView view={shown} />
      {canEdit && <SiteLayoutSwitcher live={live} shown={shown} onPreview={changePreview} onMakeLive={makeLive} />}
    </>
  );
};

export default observer(SiteHome);
